'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { userPreferencesApi } from '@/lib/api/user-preferences';
import { ApiError } from '@/lib/api/client';
import {
  DEFAULT_USER_PREFERENCES,
  financialPreferencesToCustosDefaults,
  type UserPreferencesV1,
} from '@/lib/user-preferences';
import type { CustosViabilidade } from '@/lib/api/auctions';
import { useAuth } from '@/context/AuthContext';

interface UserPreferencesContextValue {
  preferences: UserPreferencesV1;
  financialCustosDefaults: Partial<CustosViabilidade>;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<UserPreferencesV1 | null>;
  updatePreferences: (
    patch: Partial<Pick<UserPreferencesV1, 'general' | 'financial'>>
  ) => Promise<UserPreferencesV1>;
}

const UserPreferencesContext = createContext<UserPreferencesContextValue | null>(null);

export function UserPreferencesProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferencesV1>(DEFAULT_USER_PREFERENCES);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setPreferences(DEFAULT_USER_PREFERENCES);
      setError(null);
      return null;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await userPreferencesApi.get();
      setPreferences(res.preferences);
      return res.preferences;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Não foi possível carregar preferências.');
      setPreferences(DEFAULT_USER_PREFERENCES);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setPreferences(DEFAULT_USER_PREFERENCES);
      setError(null);
      return;
    }
    void refresh();
  }, [authLoading, isAuthenticated, refresh]);

  const updatePreferences = useCallback(
    async (patch: Partial<Pick<UserPreferencesV1, 'general' | 'financial'>>) => {
      const res = await userPreferencesApi.patch(patch);
      setPreferences(res.preferences);
      return res.preferences;
    },
    []
  );

  const financialCustosDefaults = useMemo(
    () => financialPreferencesToCustosDefaults(preferences.financial),
    [preferences.financial]
  );

  const value = useMemo(
    () => ({
      preferences,
      financialCustosDefaults,
      isLoading,
      error,
      refresh,
      updatePreferences,
    }),
    [preferences, financialCustosDefaults, isLoading, error, refresh, updatePreferences]
  );

  return (
    <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  const ctx = useContext(UserPreferencesContext);
  if (!ctx) {
    throw new Error('useUserPreferences deve ser usado dentro de UserPreferencesProvider');
  }
  return ctx;
}
